import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Separator } from "./ui/separator";
import { Loader2, Plus, Copy, Trash2, Link2, Ticket, Check, X, Mail, UserPlus } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const DURATIONS = [
  { label: "24 ore", hours: 24 },
  { label: "7 giorni", hours: 168 },
  { label: "30 giorni", hours: 720 },
  { label: "Mai scade", hours: 0 },
];

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

function isExpired(iso) {
  if (!iso) return false;
  try { return new Date(iso) < new Date(); } catch { return false; }
}

export default function InvitesDialog({ open, onClose }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [singleUse, setSingleUse] = useState(true);
  const [hours, setHours] = useState(168);
  const [copiedId, setCopiedId] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loadingReqs, setLoadingReqs] = useState(false);
  const [approvingId, setApprovingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/invites");
      setInvites(res.data);
    } catch (err) {
      toast.error("Impossibile caricare gli inviti");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    setLoadingReqs(true);
    try {
      const res = await api.get("/access-requests");
      setRequests(res.data);
    } catch (err) {
      console.error("requests load error", err);
    } finally {
      setLoadingReqs(false);
    }
  }, []);

  useEffect(() => { if (open) { load(); loadRequests(); } }, [open, load, loadRequests]);

  const approveRequest = async (req) => {
    setApprovingId(req.id);
    try {
      const res = await api.post(`/access-requests/${req.id}/approve`);
      const code = res.data.invite_code;
      // Copy to clipboard
      try {
        await navigator.clipboard.writeText(code);
        toast.success(`Approvata. Codice ${code} copiato.`);
      } catch {
        toast.success(`Approvata. Codice: ${code}`);
      }
      await Promise.all([load(), loadRequests()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore approvazione");
    } finally {
      setApprovingId(null);
    }
  };

  const dismissRequest = async (req) => {
    if (!window.confirm(`Ignorare la richiesta di ${req.name}?`)) return;
    try {
      await api.delete(`/access-requests/${req.id}`);
      await loadRequests();
    } catch {
      toast.error("Errore");
    }
  };

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/invites", { single_use: singleUse, expires_in_hours: hours });
      toast.success("Invito creato");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione invito");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Revocare questo invito?")) return;
    try {
      await api.delete(`/invites/${id}`);
      toast.success("Invito revocato");
      await load();
    } catch {
      toast.error("Errore revoca");
    }
  };

  const copyLink = async (code, id) => {
    const url = `${window.location.origin}/?invite=${encodeURIComponent(code)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
      toast.success("Link copiato");
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const copyCode = async (code, id) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
      toast.success("Codice copiato");
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="invites-dialog"
        className="max-w-lg rounded-3xl border-[#E8E1D9] bg-white max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full border border-[#E8E1D9] bg-[#FBF9F6] px-3 py-1">
            <Ticket className="h-3 w-3 text-[#FF7C43]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">
              Inviti famiglia
            </span>
          </div>
          <DialogTitle className="font-display text-2xl font-bold text-[#2D2926]">
            Inviti
          </DialogTitle>
          <DialogDescription className="text-[#5C5855]">
            Crea codici per far entrare zii e cugini senza condividere la password.
          </DialogDescription>
        </DialogHeader>

        {/* Create new */}
        <div className="rounded-2xl border border-[#E8E1D9] bg-[#FBF9F6] p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-[#2D2926]">Uso singolo</p>
              <p className="text-xs text-[#5C5855]">Il codice scade dopo il primo utilizzo</p>
            </div>
            <Switch
              data-testid="invite-single-use-switch"
              checked={singleUse}
              onCheckedChange={setSingleUse}
            />
          </div>
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">Durata</p>
            <div className="grid grid-cols-2 gap-2">
              {DURATIONS.map((d) => (
                <button
                  key={d.hours}
                  type="button"
                  onClick={() => setHours(d.hours)}
                  data-testid={`invite-duration-${d.hours}`}
                  className={`rounded-xl border-2 px-3 py-2 text-xs font-semibold transition ${
                    hours === d.hours
                      ? "border-[#FF7C43] bg-[#FF7C43]/10 text-[#FF7C43]"
                      : "border-[#E8E1D9] bg-white text-[#5C5855] hover:border-[#C87941]"
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <Button
            data-testid="invite-create-button"
            type="button"
            onClick={create}
            disabled={creating}
            className="w-full rounded-full bg-[#FF7C43] text-white hover:bg-[#E66A35]"
          >
            {creating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
            Genera nuovo invito
          </Button>
        </div>

        {/* Invites list */}
        <div className="mt-4 space-y-2">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
            Codici esistenti
          </p>
          {loading ? (
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded-xl bg-[#FBF9F6]" />
              ))}
            </div>
          ) : invites.length === 0 ? (
            <p className="rounded-xl border border-dashed border-[#E8E1D9] bg-white p-4 text-center text-sm text-[#5C5855]">
              Nessun invito attivo.
            </p>
          ) : (
            invites.map((inv) => {
              const expired = isExpired(inv.expires_at);
              const usedUp = inv.single_use && inv.used;
              const inactive = expired || usedUp;
              return (
                <div
                  key={inv.id}
                  data-testid={`invite-row-${inv.code}`}
                  className={`flex items-start justify-between gap-2 rounded-xl border p-3 ${
                    inactive ? "border-[#E8E1D9] bg-[#FBF9F6] opacity-60" : "border-[#E8E1D9] bg-white"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <code className="font-mono text-sm font-bold tracking-wider text-[#2D2926]">
                        {inv.code}
                      </code>
                      {usedUp && <span className="rounded-full bg-stone-200 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-stone-600">Usato</span>}
                      {expired && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-red-700">Scaduto</span>}
                      {inv.single_use && !usedUp && !expired && <span className="rounded-full bg-[#FF7C43]/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#FF7C43]">Uso singolo</span>}
                    </div>
                    <p className="mt-1 text-[10px] uppercase tracking-wider text-[#C87941]/70">
                      Scade: {formatDate(inv.expires_at) === "—" ? "Mai" : formatDate(inv.expires_at)}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {!inactive && (
                      <>
                        <button
                          data-testid={`invite-copy-code-${inv.code}`}
                          onClick={() => copyCode(inv.code, inv.id + "_c")}
                          title="Copia codice"
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E8E1D9] bg-white text-[#5C5855] hover:border-[#FF7C43] hover:text-[#FF7C43]"
                        >
                          {copiedId === inv.id + "_c" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                        </button>
                        <button
                          data-testid={`invite-copy-link-${inv.code}`}
                          onClick={() => copyLink(inv.code, inv.id + "_l")}
                          title="Copia link"
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E8E1D9] bg-white text-[#5C5855] hover:border-[#FF7C43] hover:text-[#FF7C43]"
                        >
                          {copiedId === inv.id + "_l" ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
                        </button>
                      </>
                    )}
                    <button
                      data-testid={`invite-delete-${inv.code}`}
                      onClick={() => remove(inv.id)}
                      title="Revoca"
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-red-100 bg-white text-red-500 hover:border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Access Requests Section */}
        {(requests.length > 0 || loadingReqs) && (
          <>
            <Separator className="my-4 bg-[#E8E1D9]" />
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">
                  <Mail className="h-3 w-3" />
                  Richieste di accesso
                </p>
                {requests.length > 0 && (
                  <span className="rounded-full bg-[#FF7C43] px-2 py-0.5 text-[10px] font-bold text-white">
                    {requests.length}
                  </span>
                )}
              </div>
              {loadingReqs ? (
                <div className="h-16 animate-pulse rounded-xl bg-[#FBF9F6]" />
              ) : (
                requests.map((req) => (
                  <div
                    key={req.id}
                    data-testid={`access-request-${req.id}`}
                    className="rounded-2xl border border-[#FF7C43]/30 bg-[#FF7C43]/5 p-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-[#2D2926]">{req.name}</p>
                        <p className="truncate text-xs text-[#5C5855]">{req.email}</p>
                        {req.message && (
                          <p className="mt-1 line-clamp-2 text-xs italic text-[#5C5855]">
                            "{req.message}"
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <button
                          data-testid={`access-approve-${req.id}`}
                          onClick={() => approveRequest(req)}
                          disabled={approvingId === req.id}
                          title="Approva e copia codice"
                          className="flex h-8 items-center gap-1 rounded-full bg-[#FF7C43] px-3 text-xs font-bold text-white hover:bg-[#E66A35] disabled:opacity-60"
                        >
                          {approvingId === req.id
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <UserPlus className="h-3 w-3" />}
                          Approva
                        </button>
                        <button
                          data-testid={`access-dismiss-${req.id}`}
                          onClick={() => dismissRequest(req)}
                          title="Ignora"
                          className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E8E1D9] bg-white text-[#5C5855] hover:border-red-300 hover:text-red-600"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="rounded-full border-[#E8E1D9]"
          >
            <X className="mr-1 h-4 w-4" /> Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
